// The "How it works" terminal on /build: three steps, each a scripted terminal
// session. Everything on screen comes from (step, elapsed), so the component
// keeps only those two numbers. The output is illustrative, in the CLI's words
// where the CLI prints them.

export type Tone = 'text' | 'secondary' | 'tertiary' | 'brand';

export interface ScriptLine {
  /** Typed one character at a time, or shown whole. */
  kind: 'type' | 'out';
  prefix: string;
  prefixTone?: Tone;
  text: string;
  tone?: Tone;
  /** Extra space above the line. */
  gap?: boolean;
}

export interface Step {
  num: string;
  title: string;
  command: string;
  description: string;
  /** The terminal's path label while the step plays. */
  directory: string;
  script: ScriptLine[];
}

export const TYPE_MS = 38;
export const OUT_MS = 380;
export const PAUSE_MS = 350;
export const HOLD_MS = 2600;
// The last step holds longer so the notification can be read.
export const NOTIFICATION_HOLD_MS = 1800;
export const NOTIFICATION_DELAY_MS = 500;
export const BLINK_MS = 500;
export const TICK_MS = 40;

export const APP_NAME = 'Chess Duel';
const CLONE = 'br-4-k2m9qd';

export const STEPS: Step[] = [
  {
    num: '01',
    title: 'Create',
    command: `bankroll apps create --name "${APP_NAME}"`,
    description: 'A private repo from the starter, a wallet Bankroll runs for it, and a hosted address — cloned here.',
    directory: '~',
    script: [
      { kind: 'type', prefix: '$', text: `bankroll apps create --name "${APP_NAME}"` },
      { kind: 'out', prefix: '', text: `Creating ${APP_NAME}…`, tone: 'tertiary', gap: true },
      { kind: 'out', prefix: '✓', text: 'Private repo from bankroll-starter' },
      { kind: 'out', prefix: '✓', text: 'Wallet, run by Bankroll' },
      { kind: 'out', prefix: '✓', text: 'Hosted address' },
      { kind: 'out', prefix: '✓', text: `Cloned into ./${CLONE}  (remote: bankroll)` }
    ]
  },
  {
    num: '02',
    title: 'Build',
    command: '"Build me a chess game"',
    description:
      'Start your agent in the clone and say what to build. It wires in Bankroll payments, identity, and geo, then checks its own work in a headless phone.',
    directory: `~/${CLONE}`,
    script: [
      { kind: 'type', prefix: '$', text: 'claude' },
      { kind: 'type', prefix: '›', text: 'Build me a chess game', gap: true },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'Reading AGENTS.md', tone: 'secondary', gap: true },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'Editing src/app/app/home.tsx', tone: 'secondary' },
      { kind: 'out', prefix: '✓', text: 'Payments     charge() for entry fees' },
      { kind: 'out', prefix: '✓', text: 'Identity     verified age from the session' },
      { kind: 'out', prefix: '✓', text: 'Geo          paidPlayRestriction(session)' },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'npm run check -- /app', tone: 'secondary' },
      { kind: 'out', prefix: '✓', text: 'Checks passed in a headless phone' },
      { kind: 'out', prefix: '', text: 'Your game is ready. Tell me when to push.', gap: true }
    ]
  },
  {
    num: '03',
    title: 'Push',
    command: 'git push bankroll main',
    description:
      'The push is the deploy. Bankroll reads the code, sets where it may take real money, signs its manifest, and deploys it in a minute or two.',
    directory: `~/${CLONE}`,
    script: [
      { kind: 'type', prefix: '$', text: 'git push bankroll main' },
      { kind: 'out', prefix: '', text: 'To bankroll   main -> main', tone: 'tertiary', gap: true },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'pushed   Reading the code', tone: 'secondary', gap: true },
      { kind: 'out', prefix: '', text: '         Skill game with paid entry', tone: 'tertiary' },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'pushed   Setting geo restrictions', tone: 'secondary' },
      { kind: 'out', prefix: '', text: '         Recommended: 18+, blocked where required', tone: 'tertiary' },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'pushed   Signing manifest', tone: 'secondary' },
      { kind: 'out', prefix: '●', prefixTone: 'tertiary', text: 'pushed   Deploying', tone: 'secondary' },
      { kind: 'out', prefix: '✓', text: `live     ${APP_NAME} is ready`, tone: 'brand' }
    ]
  }
];

interface TimedLine extends ScriptLine {
  start: number;
  end: number;
}

const timeline = (step: Step): TimedLine[] => {
  let t = 0;
  return step.script.map((line, i) => {
    const start = t + (i > 0 ? PAUSE_MS : 0);
    const end = start + (line.kind === 'type' ? line.text.length * TYPE_MS : OUT_MS);
    t = end;
    return { ...line, start, end };
  });
};

const TIMELINES = STEPS.map(timeline);
const NOTIFYING_STEP = STEPS.length - 1;
const scriptEnd = (step: number) => TIMELINES[step][TIMELINES[step].length - 1].end;

/** How long a step plays before autoplay moves on. */
export const stepDuration = (step: number): number =>
  scriptEnd(step) + HOLD_MS + (step === NOTIFYING_STEP ? NOTIFICATION_HOLD_MS : 0);

export interface FrameLine {
  prefix: string;
  prefixTone: Tone;
  text: string;
  tone: Tone;
  gap: boolean;
  cursor: boolean;
}

export interface Frame {
  directory: string;
  lines: FrameLine[];
  /** Per step, 0 to 1: how far the active step has played. */
  progress: number[];
  notification: boolean;
}

export interface PlayState {
  step: number;
  elapsed: number;
}

/**
 * What the terminal shows `elapsed` ms into `step`. Without autoplay (reduced
 * motion) the whole script shows at once, with a steady cursor.
 */
export function frame({ step, elapsed }: PlayState, autoplay: boolean): Frame {
  const lines = TIMELINES[step];
  const at = autoplay ? elapsed : Number.POSITIVE_INFINITY;
  const finished = at >= scriptEnd(step);
  const blinkOn = !autoplay || Math.floor(at / BLINK_MS) % 2 === 0;
  const visible = lines.filter((line) => at >= line.start);
  return {
    directory: STEPS[step].directory,
    lines: visible.map((line, i) => {
      const typing = line.kind === 'type' && at < line.end;
      const typed = Math.max(0, Math.floor((at - line.start) / TYPE_MS));
      return {
        prefix: line.prefix,
        prefixTone: line.prefixTone ?? 'brand',
        text: line.kind === 'type' ? line.text.slice(0, typed) : line.text,
        tone: line.tone ?? 'text',
        gap: line.gap ?? false,
        cursor: (typing || (finished && i === visible.length - 1)) && blinkOn
      };
    }),
    progress: STEPS.map((_, i) => (i !== step ? 0 : autoplay ? Math.min(1, elapsed / stepDuration(i)) : 1)),
    notification: step === NOTIFYING_STEP && at >= scriptEnd(step) + NOTIFICATION_DELAY_MS
  };
}

/** One tick of autoplay: the next step once this one has played, looping. */
export function advance({ step, elapsed }: PlayState): PlayState {
  const next = elapsed + TICK_MS;
  return next >= stepDuration(step) ? { step: (step + 1) % STEPS.length, elapsed: 0 } : { step, elapsed: next };
}
